import React from 'react';
import {
  AppBar,
  Box,
  Dialog,
  DialogProps,
  Divider,
  IconButton,
  Theme,
  Toolbar,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { DRAWER_Z_INDEX } from '../../definitions/constants';
import { ellipsis } from '../../theme/styles/mixins';
import { FullScreenDialogTransition } from './FullScreenDialogTransition';
import { LayoutAppBarProps } from '../Layout/LayoutAppBar';
import LayoutContent, { LayoutContentProps } from '../Layout/LayoutContent';
import Layout, { LayoutProps } from '../Layout/Layout';
import BuyerCopilotFullScreenHeader from '../BuyerCopilot/BuyerCopilotFullScreenHeader';

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(3),
  },
  ellipsis: {
    ...ellipsis(),
  },
  reduceTitleSpace: {
    // added max width to have space for centerAction in the center
    maxWidth: theme.spacing(45),
  },
}));

type ClassKey = 'appBar' | 'divider' | 'paper';

export interface FullScreenDialogProps {
  centerAction?: React.ReactNode;
  children: React.ReactNode | ((footer: React.ReactNode) => React.ReactNode);
  classes?: Partial<Record<ClassKey, string>>;
  closeButton?: React.ReactNode;
  disabled?: boolean;
  disableEscapeKeyDown?: boolean;
  ellipsis?: boolean;
  LayoutAppBarProps?: LayoutAppBarProps;
  LayoutContentProps?: LayoutContentProps;
  LayoutProps?: LayoutProps;
  onClose: () => void;
  open: boolean;
  primaryAction?: React.ReactNode;
  sx?: DialogProps['sx'];
  title: React.ReactNode;
  isBuyerCopilotEnabledForRoom?: boolean;
  onBuyerCopilotClick?: () => void;
}
export type PickedFullScreenDialogProps = Pick<
  FullScreenDialogProps,
  'onClose' | 'open'
>;

const FullScreenDialog: React.FC<FullScreenDialogProps> = (
  props: FullScreenDialogProps
): JSX.Element => {
  const classes = useStyles();

  const {
    centerAction,
    children,
    classes: classesProp,
    closeButton,
    disabled = false,
    disableEscapeKeyDown = false,
    ellipsis,
    LayoutAppBarProps,
    LayoutContentProps,
    LayoutProps,
    onClose,
    open,
    primaryAction,
    sx,
    title,
    isBuyerCopilotEnabledForRoom,
    onBuyerCopilotClick,
  } = props;

  const renderCloseButton = () =>
    closeButton ? (
      <Box onClick={onClose} data-testid="button-close">
        {closeButton}
      </Box>
    ) : (
      <IconButton
        aria-label="close"
        color="inherit"
        disabled={disabled}
        edge="start"
        onClick={onClose}
        size="large"
        data-testid="button-close"
      >
        <Close />
      </IconButton>
    );

  const renderTitle = () => {
    if (!title) {
      return <Box flex={centerAction ? 'inherit' : 1} />;
    }

    if (typeof title !== 'string') {
      return (
        <Box
          flex="1"
          ml={2}
          sx={{
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {title}
        </Box>
      );
    }

    return (
      <Box flex="1" ml={2}>
        <Typography
          className={classNames({
            [classes.ellipsis]: ellipsis,
            [classes.reduceTitleSpace]: centerAction,
          })}
          color="inherit"
          title={title}
          variant="h6"
        >
          {title}
        </Typography>
      </Box>
    );
  };

  const renderCenterAction = () => {
    if (!centerAction) {
      return;
    }

    return (
      <Box
        display="flex"
        flex="auto"
        justifyContent={title ? 'flex-start' : 'center'}
      >
        {centerAction}
      </Box>
    );
  };

  return (
    <Dialog
      classes={{ paper: classesProp?.paper }}
      disableEscapeKeyDown={disableEscapeKeyDown}
      fullScreen
      onClose={onClose}
      open={open}
      sx={sx || { zIndex: DRAWER_Z_INDEX }}
      TransitionComponent={FullScreenDialogTransition}
      disableEnforceFocus={isBuyerCopilotEnabledForRoom}
    >
      <Layout
        appBar={
          <AppBar
            className={classesProp?.appBar}
            color="transparent"
            position="static"
          >
            <Toolbar>
              {renderCloseButton()}
              <Divider
                className={classNames(classes.divider, classesProp?.divider)}
                flexItem
                orientation="vertical"
              />
              {renderTitle()}
              {renderCenterAction()}
              {isBuyerCopilotEnabledForRoom && (
                <BuyerCopilotFullScreenHeader onClick={onBuyerCopilotClick!} />
              )}
              {primaryAction}
            </Toolbar>
          </AppBar>
        }
        disableAppBarRegistration
        LayoutAppBarProps={LayoutAppBarProps}
        {...LayoutProps}
      >
        {(footer) => (
          <LayoutContent {...LayoutContentProps}>
            {typeof children === 'function' ? children(footer) : children}
          </LayoutContent>
        )}
      </Layout>
    </Dialog>
  );
};

export default FullScreenDialog;
